import "./KCalendar.scss";
import KCalendarDepartmentFilter from "./KCalendarDepartmentFilter";
import Themeable from "vuetify/lib/mixins/themeable";
import { Broadcastable, ToolbarActionable } from "kinnect-mixins";
import { CalendarEvent } from "kinnect-models";
import { CalendarView } from "vue-simple-calendar";
import { KActionDialog, KDateTimeRange } from "kinnect-general";
import {
	VBtn,
	VCard,
	VCardActions,
	VCardText,
	VCardTitle,
	VContainer,
	VDialog,
	VCol,
	VIcon,
	VList,
	VListItem,
	VListItemAction,
	VListItemContent,
	VRow,
	VSpacer,
	VTooltip
} from "vuetify/lib";
import { formatDate, formatTime } from "kinnect-formatters";

export default {
	name: "KCalendar",

	mixins: [
		Broadcastable,
		Themeable,
		ToolbarActionable
	],

	data: () => ( {
		showDate: new Date(),
		eventDialog: {
			open: false,
			event: null
		},
		selectedDepartmentsUsersIds: [],
		events: [],
		idHoveredEvent: null,
		broadcasts: {
			Model: CalendarEvent,
			channel: "calendar",
			propKey: "events",
			eventNames: {
				c: "calendar.created",
				u: "calendar.updated",
				d: "calendar.deleted"
			}
		},
		toolbarActions: {
			icon: "add_circle",
			text: "Add to Calendar",
			to: { name: "calendarEventAdd" }
		}
	} ),

	computed: {
		idUser() {
			return this.$store.getters["auth/userID"];
		},

		formattedEvents() {
			return this.events.map( event => {
				const startTime = event.$.startTime || "00:00:00";
				const endTime = event.$.endTime || "00:00:00";

				event.startDate = new Date( `${event.$.startDate} ${startTime}` );
				event.endDate = new Date( `${event.$.endDate} ${endTime}` );

				if ( event.idUser === this.idUser || this.$can( "browse_admin" ) ) {
					event.classes = [ "cv-event--owned" ];
				}

				return event;
			} );
		},

		filteredEvents() {
			return this.formattedEvents.filter( event =>
				this.selectedDepartmentsUsersIds.indexOf( event.idUser ) !== -1
			);
		},

		genCalendar() {
			return <CalendarView
				class={{
					"theme-kinnect": true,
					...this.themeClasses
				}}
				eventContentHeight="1.4em"
				eventTop="2em"
				events={this.filteredEvents}
				on={{
					"click-event": event => {
						this.eventDialog.event = event.originalEvent;
						this.eventDialog.open = true;
					}
				}}
				periodChangedCallback={data => {
					this.fetchEvents( data.displayFirstDate, data.displayLastDate );
				}}
				scopedSlots={{ header: this.genHeader }}
				showDate={this.showDate}
				showEventTimes
				timeFormatOptions={{
					hour: "numeric",
					minute: "2-digit"
				}}
			></CalendarView>;
		},

		genEventDialog() {
			const hasActionPermissions = this.eventDialog.event
				&& ( this.eventDialog.event.idUser === this.idUser
					|| this.$can( "browse_admin" ) );

			return <VDialog maxWidth={500} vModel={this.eventDialog.open}>
				{this.eventDialog.event && <VCard>
					<VCardTitle class="primary white--text">
						<span class="text-subtitle-1">{this.eventDialog.event.title}</span>
					</VCardTitle>

					<VCardText>
						<VList>
							<VListItem>
								<VListItemAction>
									<VIcon>access_time</VIcon>
								</VListItemAction>

								<VListItemContent>
									<KDateTimeRange
										allDay={Boolean( this.eventDialog.event.allDay )}
										endDate={this.eventDialog.event.endDate}
										endTime={this.eventDialog.event.endTime}
										startDate={this.eventDialog.event.startDate}
										startTime={this.eventDialog.event.startTime}
									></KDateTimeRange>
								</VListItemContent>
							</VListItem>

							{this.eventDialog.event.description && <VListItem>
								<VListItemAction>
									<VIcon>sort</VIcon>
								</VListItemAction>

								<VListItemContent>
									{this.eventDialog.event.description}
								</VListItemContent>
							</VListItem>}
						</VList>
					</VCardText>

					<VCardActions>
						{hasActionPermissions && [
							<VSpacer></VSpacer>,

							<VBtn
								color="link"
								text
								to={{
									name: "calendarEventEdit",
									params: { idCalendarEvent: this.eventDialog.event.id }
								}}
							>Edit</VBtn>,

							<KActionDialog
								confirmButtonColor="error"
								confirmButtonText="Delete"
								loading={this.$wait.is( "delete calendar event" )}
								onConfirm={() =>
									this.deleteCalendarEvent( this.eventDialog.event ).then( () => {
										this.eventDialog.open = false;
									} )
								}
								scopedSlots={{
									activator: ( { on } ) =>
										<VBtn
											color="error"
											on={on}
											text
										>Delete</VBtn>
								}}
								title="Delete Calendar Event"
							>
								Are you sure you want to delete the calendar event <strong>{this.eventDialog.event.title}</strong>?
							</KActionDialog>
						]}
					</VCardActions>
				</VCard>}
			</VDialog>;
		}
	},

	methods: {
		genEventTitle( event ) {
			if ( event.originalEvent.allDay ) {
				return event.title;
			}

			return `${event.title} (${formatTime( event.originalEvent.startTime )} - ${formatTime( event.originalEvent.endTime )})`;
		},

		genEvent( { event, top } ) {
			const eventTitle = this.genEventTitle( event );

			return <div
				class={[
					event.classes,
					"cv-event",
					{ "cv-event--owned": event.originalEvent.idUser === this.idUser }
				]}
				key={`${event.id}-event`}
				onClick={() => {
					this.eventDialog.event = event.originalEvent;
					this.eventDialog.open = true;
				}}
				onMouseenter={() => this.idHoveredEvent = event.id}
				onMouseleave={() => this.idHoveredEvent = null}
				style={{ top }}
				title={eventTitle}
			>
				{eventTitle}
			</div>;
		},

		genHeader( { headerProps } ) {
			return <VContainer class="primary white--text" fluid>
				<VRow>
					<VCol class="text-h4 text-no-wrap shrink">
						{formatDate( headerProps.periodStart, "MMMM yyyy" )}
					</VCol>

					<VCol class="text-right text-no-wrap">
						<VTooltip
							bottom
							scopedSlots={{
								activator: ( { on } ) =>
									<VBtn
										class="currentPeriod"
										dark
										disabled={headerProps.currentPeriod.getTime() === headerProps.periodStart.getTime()}
										icon
										on={on}
										onClick={() => this.setShowDate( headerProps.currentPeriod )}
										outlined
									><VIcon>home</VIcon></VBtn>
							}}
						>{formatDate( new Date(), "MMMM yyyy" )}</VTooltip>

						<VTooltip
							bottom
							scopedSlots={{
								activator: ( { on } ) =>
									<VBtn
										class="previousPeriod mx-3"
										dark
										disabled={!headerProps.previousPeriod}
										icon
										on={on}
										onClick={() => this.setShowDate( headerProps.previousPeriod )}
										outlined
									><VIcon>chevron_left</VIcon></VBtn>
							}}
						>Previous month</VTooltip>

						<VTooltip
							bottom
							scopedSlots={{
								activator: ( { on } ) =>
									<VBtn
										class="nextPeriod"
										dark
										disabled={!headerProps.nextPeriod}
										icon
										on={on}
										onClick={() => this.setShowDate( headerProps.nextPeriod )}
										outlined
									><VIcon>chevron_right</VIcon></VBtn>
							}}
						>Next month</VTooltip>
					</VCol>
				</VRow>

				<VRow>
					<VCol cols={12}>
						<KCalendarDepartmentFilter
							onUpdateUserIds={val => this.selectedDepartmentsUsersIds = val}
						></KCalendarDepartmentFilter>
					</VCol>
				</VRow>
			</VContainer>;
		},

		deleteCalendarEvent( CalEvent ) {
			const deleteIndex = this.events.findIndex( item => CalEvent.id === item.id );

			return CalEvent.delete().then( () => {
				this.$delete( this.events, deleteIndex );
			} );
		},

		setShowDate( showDate ) {
			this.showDate = showDate;
		},

		async fetchEvents( startDate, endDate ) {
			if ( startDate && endDate ) {
				this.events = await CalendarEvent.params( {
					start: formatDate( startDate, "yyyy-MM-dd" ),
					end: formatDate( endDate, "yyyy-MM-dd" )
				} ).get();
			}
		}
	},

	render() {
		return <div>
			{this.genCalendar}
			{this.genEventDialog}
		</div>;
	}
};
